import { Component, NgZone, AfterContentChecked } from '@angular/core';
import { AlertController, IonicSafeString, MenuController, Platform } from '@ionic/angular';
import { ToastrService } from 'ngx-toastr';
import { OfflineService } from './services/offline/offline.service';
import { UserSessionService } from './services/util/user-session.service';
import { SettingsProvider } from './providers/settings/settings';
import { UiService } from './services/util/ui.service';
import { Router } from '@angular/router';
import { LoadingService } from './services/util/loading.service';
import { EventsService } from './services/util/events.service';
import { MenuItem } from '../assets/model/MenuItem';
import { NotificationComponent } from './components/notification-component/notification-component';
import { AboutPage } from './pages/about/about.page';
import { DashboardPage } from './pages/dashboard/dashboard.page';
import { APP_CONFIG } from '../environments/app_config';
import {TranslateService} from '@ngx-translate/core';
import { AppSettings } from './pages/app-settings/app-settings.page';
import { LocalTranslationService } from './services/util/localtranslation.service';
import { firstValueFrom } from 'rxjs';

// eslint-disable-next-line @typescript-eslint/naming-convention
declare let Offline;

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    standalone: false,
})
export class AppComponent implements AfterContentChecked {
    public isOnline = true;
    public errorToast;
    dashboard=''
    syncLog=''
    about=''
    applicationDownloadedMsg=''
    aNewVersionIsAvailableMsg=''
    appInformation=''
    appVersion=''
    appEnvironment=''
    appApiBasePath=''
    lastReleaseTitle=''
    lastReleaseDate=''
    close=''
    sessionExpiredMsg=''
    userIsDisabledInIices=''
    currentSelectedLang=''
    pages: MenuItem[] = [
        {
            title: 'Dashboard',
            component: DashboardPage,
            icon: 'home-outline',
            route: '/home/map',
            allowedRoles: 'inspector',
        },
        {
            title: 'Sync log',
            component: NotificationComponent,
            icon: 'sync-outline',
            route: '/home/sync-log',
            allowedRoles: 'inspector',
        },
        {
            title: 'Settings',
            component: AppSettings,
            icon: 'settings-outline',
            route: '/home/settings',
            allowedRoles: 'inspector',
        },
        {
            title: 'About',
            component: AboutPage,
            icon: 'information-circle-outline',
            action: () => {
                this.menuCtrl.toggle();
                this.displayAppInfo();
            },
            allowedRoles: 'inspector',
            lines: 'none',
        },
    ];
    selectedTheme: string;
    isLoggedIn = false;

    itemIndexSelected = 0;
    selectedLanguage ='en';
    settingsLabel='';
    
    constructor(
        private alertController: AlertController,
        private platform: Platform,
        // private splashScreen: SplashScreen,
        // private statusBar: StatusBar,
        public zone: NgZone,
        private toastr: ToastrService,
        private sessionService: UserSessionService,
        private settings: SettingsProvider,
        public router: Router,
        private events: EventsService,
        private uiService: UiService,
        private loadingService: LoadingService,
        private userSessionService: UserSessionService,
        public menuCtrl: MenuController,
        public translate: TranslateService,
        private localTranslateService: LocalTranslationService
    ) {
        let currentUser = JSON.parse(localStorage.getItem('currentUser'));
        this.selectedLanguage = currentUser?.selectedLanguage === 'F' ? 'fr' : 'en';
        this.translate.setDefaultLang(this.selectedLanguage);
        this.translate.use(this.selectedLanguage);

        this.translateStateData().then(
            ()=>{
                // this.initializeApp().then();
                this.pages[0].title = this.dashboard;
                this.pages[1].title = this.syncLog;
                this.pages[2].title= this.settingsLabel;
                this.pages[3].title = this.about;
                this.initialize();
            }
        )
    }

    initialize() {
        this.initializeApp().then();

        window.addEventListener('beforeinstallprompt', (e) => {
            // Prevent the mini-infobar from appearing on mobile
            e.preventDefault();
            this.toastr.success(this.applicationDownloadedMsg, '', {
                positionClass: 'toast-top-full-width',
                timeOut: 0,
                closeButton: true,
                tapToDismiss: false,
                extendedTimeOut: 0,
            });
        });

        Offline.off('up', this.onlineHandler.bind(this));
        Offline.off('down', this.offlineHandler.bind(this));
        Offline.on('up', this.onlineHandler.bind(this));
        Offline.on('down', this.offlineHandler.bind(this));

        OfflineService.initOfflineChecks();
        Offline.check();
    }

    ngAfterInit() {
        this.isOnline = Offline.state !== 'down';
    }

    async ngAfterContentChecked() {
        const currentLang = this.translate.currentLang;
        if(currentLang !== this.currentSelectedLang) {
            await this.translateStateData();
        }
        this.pages[0].title = this.dashboard;
        this.pages[1].title = this.syncLog;
        this.pages[2].title= this.settingsLabel;
        this.pages[3].title = this.about;
    }

    onlineHandler() {
        this.zone.run(() => {
            this.isOnline = true;
            OfflineService.isOffline = false;
            if (this.errorToast) {
                this.toastr.clear(this.errorToast.toastId);
            }
            setTimeout(() => {
                this.uiService.internetConnectionToast('on');
            }, 500);
        });
    }

    offlineHandler() {
        this.zone.run(() => {
            this.isOnline = false;
            OfflineService.isOffline = true;

            this.errorToast = this.uiService.internetConnectionToast('off');
        });
    }

    initListenForUpdateAvailable() {
        /** in case PWA loads slow */
        if ((window as any).updateJustInstalled === true) {
            this.uiService.presentToast(
                this.aNewVersionIsAvailableMsg,
                'succ'
            );
        }

        if ((window as any).isUpdateAvailable) {
            // listen to the service worker promise in index.html to see if there has been a new update.
            // condition: the service-worker.js needs to have some kind of change - e.g. increment CACHE_VERSION.
            (window as any).isUpdateAvailable.then((isAvailable) => {
                if (isAvailable) {
                    this.uiService.presentToast(
                        this.aNewVersionIsAvailableMsg,
                        'succ'
                    );
                }
            });
        }
    }

    removeUnauthorizedItemsFromMenu() {
        const currentUser = this.userSessionService.getCurrentUser();
        if (currentUser) {
            this.pages = this.pages.filter(
                (page) => page.allowedRoles.toLowerCase() === currentUser.post.toLowerCase()
            );
        }
    }

    async openPage(page, index) {
        this.itemIndexSelected = index;
        await this.router.navigateByUrl(page.route);
    }

    private toggleMenu() {}

    private displayAppInfo() {
        const selectedLanguage = this.localTranslateService.getSelectedLanguage();
        const rowClass = selectedLanguage === 'fr' ? 'info-row2' : 'info-row1';
        const widthClass = selectedLanguage === 'fr' ? 'alert-width-fr' : 'alert-width-eng';
        this.alertController
            .create({
                header: this.appInformation,
                cssClass: `app-info-about-alert ${widthClass}`,
                message: new IonicSafeString(`
                    <div class="${rowClass}">
                        <span class="info-label"><strong>${this.appEnvironment}</strong></span>
                        <span class="info-value">${APP_CONFIG.ENVIRONMENT.type?.toUpperCase()}</span>
                    </div>

                    <div class="${rowClass}">
                        <span class="info-label"><strong>${this.appApiBasePath}</strong></span>
                        <span class="info-value">
                            ${APP_CONFIG.ENVIRONMENT.type?.toLowerCase() === 'dev'
                            ? 'api-dev.munichre.com'
                            : new URL(APP_CONFIG.ENVIRONMENT.API_DETAILS.AUTH_ROOT).hostname}
                        </span>
                    </div>

                    <div class="${rowClass}">
                        <span class="info-label"><strong>${this.lastReleaseTitle}</strong></span>
                        <span class="info-value">${APP_CONFIG.lastReleaseTitle}</span>
                    </div>

                    <div class="${rowClass}">
                        <span class="info-label"><strong>${this.lastReleaseDate}</strong></span>
                        <span class="info-value">${APP_CONFIG.lastReleaseDate}</span>
                    </div>
                `),
                buttons: [{ role: 'cancel', text: this.close }],
            })
            .then(alert => alert.present());
    }

    private async initializeApp() {
        await this.platform.ready();
        // this.statusBar.styleDefault();
        // this.splashScreen.hide();

        this.events.subscribe('user:logout', () => {
            this.sessionService.clearUserSession();
            this.uiService.presentToast(this.sessionExpiredMsg, 'err');
            this.router.navigate(['/login']);
            this.loadingService.hideLoader();
        });

        this.events.subscribe('user:notActive', () => {
            this.sessionService.clearUserSession();
            this.uiService.presentToast(this.userIsDisabledInIices, 'err');
            this.router.navigate(['/login']);
            this.loadingService.hideLoader();
        });

        this.isLoggedIn = this.sessionService.getUserSession() !== undefined;

        if (this.isLoggedIn) {
            this.router.navigate(['/dashboard']);
        } else {
            this.router.navigate(['/login']);
        }

        this.initListenForUpdateAvailable();
    }

    private async translateStateData(): Promise<void> {
        this.currentSelectedLang = this.translate.currentLang;

        const text = await firstValueFrom(
            this.translate.get([
                'app.dashboard',
                'app.syncLog',
                'app.about',
                'app.applicationDownloadedMsg',
                'app.aNewVersionIsAvailableMsg',
                'app.appInformation',
                'app.appApiBasePath',
                'app.lastReleaseTitle',
                'app.lastReleaseDate',
                'app.appVersion',
                'app.close',
                'app.sessionExpiredMsg',
                'app.userIsDisabledInIices',
                'app.appEnvironment',
                'app.settings'
            ])
        );

        this.dashboard = text['app.dashboard'];
        this.syncLog = text['app.syncLog'];
        this.about = text['app.about'];
        this.applicationDownloadedMsg = text['app.applicationDownloadedMsg'];
        this.aNewVersionIsAvailableMsg = text['app.aNewVersionIsAvailableMsg'];
        this.appInformation = text['app.appInformation'];
        this.appApiBasePath = text['app.appApiBasePath'];
        this.lastReleaseTitle = text['app.lastReleaseTitle'];
        this.lastReleaseDate = text['app.lastReleaseDate'];
        this.appVersion = text['app.appVersion'];
        this.close = text['app.close'];
        this.sessionExpiredMsg = text['app.sessionExpiredMsg'];
        this.userIsDisabledInIices = text['app.userIsDisabledInIices'];
        this.appEnvironment = text['app.appEnvironment'];
        this.settingsLabel = text['app.settings'];
    }
}
